import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    CubeMgmtState,
    Library,
    Scope,
    Task,
    MasterSourceState,
    DetailSourceState,
    Report,
    SegmentSourceState,
    Cube
} from "./types";
import { levels, detailTypes, partitionLevels } from "common/enums";

const defaultFilter = {
    pageNumber: 1,
    pageSize: 10,
};

const initialState: CubeMgmtState = {
    masterSelected: [],
    detailSelected: [],
    masterLevel: levels.cube,
    detailLevel: detailTypes.linked,
    partitionLevel: partitionLevels.reports,
    scenarios: [],
    cubes: [],
    scopes: [],
    hasPendingChanges: false,
    task: null,
    libraries: [],
    reportsEditable: false,

    // master sources
    masterSource: {
        data: [],
        total: 0,
        filter: {
            ...defaultFilter
        }
    },

    // detail sources
    detailSource: {
        data: [],
        total: 0,
        filter: {
            ...defaultFilter
        }
    },

    // partition sources
    segmentSource: {
        data: [],
        total: 0,
        filter: {
            ...defaultFilter
        }
    },

    // reports source
    reportSource: {
        data: []
    },

    // spinners
    generalSpinner: false,
    masterSpinner: false,
    detailSpinner: false,
    partitionSpinner: false,

    cubeEditModal: {
        open: false,
        locked: false,
        header: '',
        source: {
            cubeId: null,
            cubeName: '',
            description: null
        }
    }
};

const cubeMgmtSlice = createSlice({
    name: "CubeMgmt",
    initialState,
    reducers: {
        setMasterSource(state, action: PayloadAction<MasterSourceState>) {
            state.masterSource.data = action.payload.data;
            state.masterSource.total = action.payload.total;
            state.masterSource.filter = { ...state.masterSource.filter, ...action.payload.filter }
        },
        setDetailSource(state, action: PayloadAction<DetailSourceState>) {
            state.detailSource.data = action.payload.data;
            state.detailSource.total = action.payload.total;
            state.detailSource.filter = { ...state.detailSource.filter, ...action.payload.filter }
        },
        setSegmentsSource(state, action: PayloadAction<SegmentSourceState>) {
            state.segmentSource.data = action.payload.data;
            state.segmentSource.total = action.payload.total;
            state.segmentSource.filter = { ...state.segmentSource.filter, ...action.payload.filter }
        },
        setReportsSource(state, action: PayloadAction<{ data: Report[] }>) {
            state.reportSource.data = action.payload.data;
        },
        setLookups(state, action: PayloadAction<{ libraries: Library[]; scopes: Scope[]; task: Task }>) {
            state.libraries = action.payload.libraries;
            state.scopes = action.payload.scopes;
            state.task = action.payload.task;
        },
        setMasterFilter(state, action: PayloadAction<Partial<MasterSourceState["filter"]>>) {
            state.masterSource.filter = { ...state.masterSource.filter, ...action.payload };
        },
        setDetailFilter(state, action: PayloadAction<Partial<DetailSourceState["filter"]>>) {
            state.detailSource.filter = { ...state.detailSource.filter, ...action.payload };
        },
        setSegmentFilter(state, action: PayloadAction<Partial<SegmentSourceState["filter"]>>) {
            state.segmentSource.filter = { ...state.segmentSource.filter, ...action.payload };
        },
        setDefaultReportsFlag(state, action: PayloadAction<boolean>) {
            state.task!.hasDefaultReports = action.payload
        },
        // level switch
        changeMasterLevel(state, action: PayloadAction<number>) {
            state.masterLevel = action.payload;
            state.masterSelected = [];
            state.detailSelected = [];
            state.hasPendingChanges = false;
            state.masterSource = {
                ...initialState.masterSource,
                filter: { ...state.masterSource.filter }
            };
            state.detailSource = { ...initialState.detailSource };
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        changeDetailLevel(state, action: PayloadAction<number>) {
            state.detailLevel = action.payload;
            state.detailSelected = [];
            state.hasPendingChanges = false;
            state.detailSource = {
                ...initialState.detailSource,
                filter: { ...state.detailSource.filter }
            };
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        changePartitionLevel(state, action: PayloadAction<number>) {
            state.partitionLevel = action.payload;
            state.segmentSource = {
                ...initialState.segmentSource,
                filter: { ...state.segmentSource.filter }
            };
            state.reportSource = { ...initialState.reportSource };
        },
        // selected
        changeMasterSelection(state, action: PayloadAction<number[]>) {
            state.masterSelected = action.payload;
        },
        changeDetailSelection(state, action: PayloadAction<number[]>) {
            state.detailSelected = action.payload;
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        // checked
        changeMasterChecked(state, action: PayloadAction<number[]>) {
            state.masterSelected = action.payload;
        },
        changeDetailChecked(state, action: PayloadAction<number[]>) {
            state.detailSelected = action.payload;
        },
        setPendingChanges(state, action: PayloadAction<boolean>) {
            state.hasPendingChanges = action.payload;
        },
        // reports
        onReportsDeleted(state) {
            state.detailSelected = [];
            state.reportSource = { ...initialState.reportSource };
        },
        updateReports(state, action: PayloadAction<Report[]>) {
            state.hasPendingChanges = true;
            state.reportSource.data = action.payload;
        },
        changeReportsEditable(state, action: PayloadAction<boolean>) {
            state.reportsEditable = action.payload;
        },
        openCubeEditModal(state, action: PayloadAction<{ header: string, cube?: Partial<Cube> }>) {
            state.cubeEditModal.open = true;
            state.cubeEditModal.header = action.payload.header;
            if (action.payload.cube) state.cubeEditModal.source = { ...state.cubeEditModal.source, ...action.payload.cube }
        },
        toggleCubeEditModalSpinner(state, action: PayloadAction<boolean>) {
            state.cubeEditModal.locked = action.payload;
        },
        changeCubeEditModalField(state, action: PayloadAction<{ [key: string]: string }>) {
            state.cubeEditModal.source = { ...state.cubeEditModal.source, ...action.payload }
        },
        closeCubeEditModal(state) {
            state.cubeEditModal = { ...initialState.cubeEditModal }
        },
        // tab spinners
        toggleMasterSpinner(state, action: PayloadAction<boolean>) {
            state.masterSpinner = action.payload;
        },
        toggleDetailSpinner(state, action: PayloadAction<boolean>) {
            state.detailSpinner = action.payload;
        },
        togglePartitionSpinner(state, action: PayloadAction<boolean>) {
            state.partitionSpinner = action.payload;
        },
        // resets
        resetMasterSource(state) {
            state.masterSelected = [];
            state.detailSelected = [];
            state.hasPendingChanges = false;
            state.masterSource = { ...initialState.masterSource };
            state.detailSource = { ...initialState.detailSource };
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        resetDetailSource(state) {
            state.detailSelected = [];
            state.detailSource = { ...initialState.detailSource };
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        resetSegmentsSource(state) {
            state.segmentSource = { ...initialState.segmentSource };
            state.reportSource = { ...initialState.reportSource };
        },
        resetAll(state) {
            state = { ...initialState };
        }
    },
});

export const actions = cubeMgmtSlice.actions;
export default cubeMgmtSlice.reducer;
